import { useState } from 'react';
import { Edit, X } from 'lucide-react';
import api from '../../../api/axios';
import { BUSINESS_TYPES } from './constants';
import LogoUploadField from './LogoUploadField';
import StaffManagement from '../../business/StaffManagement';

const EditBusinessForm = ({ business, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    name: business.name || '',
    subdomain: business.subdomain || '',
    email: business.email || '',
    phone: business.phone || '',
    business_type: business.business_type || 'other',
    business_hours_start: business.business_hours_start || '09:00',
    business_hours_end: business.business_hours_end || '18:00',
    timezone: business.timezone || 'Europe/Berlin',
    email_from_name: business.email_from_name || '',
    email_from_address: business.email_from_address || '',
    primary_color: business.primary_color || '#3B82F6',
    logo_url: business.logo_url || '',
    logo: null,
  });
  const [loading, setLoading] = useState(false);
  const [logoOption, setLogoOption] = useState(() =>
    business.logo_url && String(business.logo_url).trim()
      ? 'url'
      : business.logo
      ? 'file'
      : 'url'
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (formData.logo instanceof File) {
        const submitData = new FormData();
        Object.keys(formData).forEach((key) => {
          if (key === 'logo') {
            if (formData.logo) submitData.append('logo', formData.logo);
          } else if (formData[key] !== null && formData[key] !== '') {
            submitData.append(key, formData[key]);
          }
        });
        await api.put(`businesses/${business.id}/`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        const { logo, ...rest } = formData;
        await api.put(`businesses/${business.id}/`, rest);
      }
      onSuccess();
    } catch (error) {
      console.error('Error updating business:', error);
      const errData = error.response?.data;
      if (errData && typeof errData === 'object') {
        const messages = Object.entries(errData)
          .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
          .join('\n');
        alert(`Error updating business:\n${messages}`);
      } else {
        alert('Error updating business. Please check all fields.');
      }
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Subdomain</label>
          <input
            type="text"
            name="subdomain"
            value={formData.subdomain}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="salon"
            required
          />
          <p className="text-xs text-gray-500 mt-1">{formData.subdomain}.reservo-tani.com</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Opening Time</label>
          <input
            type="time"
            name="business_hours_start"
            value={formData.business_hours_start}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Closing Time</label>
          <input
            type="time"
            name="business_hours_end"
            value={formData.business_hours_end}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
        <input
          type="text"
          name="timezone"
          value={formData.timezone}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Europe/Berlin"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email From Name</label>
          <input
            type="text"
            name="email_from_name"
            value={formData.email_from_name}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Business Name"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email From Address (optional)
          </label>
          <input
            type="email"
            name="email_from_address"
            value={formData.email_from_address}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="noreply@example.com"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
        <div className="flex items-center gap-2 max-w-md">
          <input
            type="color"
            name="primary_color"
            value={formData.primary_color}
            onChange={handleChange}
            className="h-10 w-14 rounded border border-gray-300 cursor-pointer"
          />
          <input
            type="text"
            value={formData.primary_color}
            onChange={(e) => setFormData((prev) => ({ ...prev, primary_color: e.target.value }))}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="#3B82F6"
          />
        </div>
      </div>

      <LogoUploadField
        logoOption={logoOption}
        setLogoOption={setLogoOption}
        logoUrl={formData.logo_url}
        onUrlChange={(val) => setFormData((prev) => ({ ...prev, logo_url: val }))}
        onFileChange={(file) => setFormData((prev) => ({ ...prev, logo: file }))}
        selectedFile={formData.logo}
        currentLogo={business.logo}
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Business Type</label>
        <select
          name="business_type"
          value={formData.business_type}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {BUSINESS_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      </div>

      <div className="border-t pt-4 mt-2">
        <StaffManagement businessId={business.id} />
      </div>

      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center space-x-2"
        >
          <X className="w-4 h-4" />
          <span>Cancel</span>
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 flex items-center space-x-2"
        >
          <Edit className="w-4 h-4" />
          <span>{loading ? 'Updating...' : 'Update Business'}</span>
        </button>
      </div>
    </form>
  );
};

export default EditBusinessForm;
